<script setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'

const GOOGLE_MAP_API_KEY = 'AIzaSyAJKEbR8rBb0h4FZYo8GePSjbI9JmbWsmY'
const COTONOU_CENTER = { lat: 6.3654, lng: 2.4183 }

const props = defineProps({
  riders: {
    type: Array,
    default: () => [],
  },
  height: {
    type: String,
    default: '100%',
  },
})

const mapContainer = ref(null)
const mapStatus = ref('loading') // loading | ready | error
let mapInstance = null
const riderMarkers = new Map() // riderId -> marker

// ---- Google Maps Init ----
async function loadGoogleMapsSDK() {
  if (window.google?.maps) return
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`
    script.async = true
    script.defer = true
    script.onload = resolve
    script.onerror = () => reject(new Error('Google Maps SDK failed to load'))
    document.head.appendChild(script)
  })
}

function initMap() {
  mapInstance = new window.google.maps.Map(mapContainer.value, {
    zoom: 13,
    center: COTONOU_CENTER,
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    styles: [
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
  })
}

function getMarkerIcon(rider) {
  const color = rider.status === 'online' ? '#22c55e'
    : rider.status === 'busy' ? '#f97316'
    : '#94a3b8'

  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 9,
  }
}

function updateMarkers(riders) {
  if (!mapInstance || !window.google?.maps) return

  const activeIds = new Set()

  riders.forEach(rider => {
    const loc = rider.location
    if (!loc?.lat || !loc?.lng) return

    activeIds.add(rider.id)
    const position = { lat: Number(loc.lat), lng: Number(loc.lng) }

    if (riderMarkers.has(rider.id)) {
      // Update existing marker
      const marker = riderMarkers.get(rider.id)
      marker.setPosition(position)
      marker.setIcon(getMarkerIcon(rider))
    } else {
      // Create new marker
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstance,
        icon: getMarkerIcon(rider),
        title: rider.name,
      })

      // Info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="font-family: sans-serif; min-width: 160px; padding: 4px 0;">
            <div style="font-weight: 700; font-size: 14px;">${rider.name}</div>
            <div style="color: #6b7280; font-size: 12px;">${rider.phoneNumber || ''}</div>
            <div style="margin-top: 6px;">
              <span style="
                background: ${rider.status === 'online' ? '#dcfce7' : rider.status === 'busy' ? '#ffedd5' : '#f1f5f9'};
                color: ${rider.status === 'online' ? '#16a34a' : rider.status === 'busy' ? '#ea580c' : '#475569'};
                padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600;
              ">${rider.status === 'online' ? 'En ligne' : rider.status === 'busy' ? 'En course' : 'Hors ligne'}</span>
            </div>
            ${loc.updatedAt ? `<div style="color: #94a3b8; font-size: 10px; margin-top: 4px;">Mis à jour ${new Date(loc.updatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>` : ''}
          </div>
        `,
      })

      marker.addListener('click', () => {
        infoWindow.open(mapInstance, marker)
      })

      riderMarkers.set(rider.id, marker)
    }
  })

  // Remove stale markers
  riderMarkers.forEach((marker, riderId) => {
    if (!activeIds.has(riderId)) {
      marker.setMap(null)
      riderMarkers.delete(riderId)
    }
  })
}

onMounted(async () => {
  try {
    await loadGoogleMapsSDK()
    initMap()
    mapStatus.value = 'ready'
    updateMarkers(props.riders)
  } catch (e) {
    console.error('[RiderMapTracking] Map init error:', e)
    mapStatus.value = 'error'
  }
})

onBeforeUnmount(() => {
  riderMarkers.forEach(m => m.setMap(null))
  riderMarkers.clear()
  mapInstance = null
})

watch(() => props.riders, (riders) => {
  if (mapStatus.value === 'ready') updateMarkers(riders)
}, { deep: true })
</script>

<template>
  <div class="relative w-full h-full rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
    <!-- Map -->
    <div ref="mapContainer" class="w-full h-full bg-gray-100"></div>

    <!-- Loading Overlay -->
    <div v-if="mapStatus === 'loading'" class="absolute inset-0 flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3"></div>
        <p class="text-sm text-gray-500">Chargement de la carte…</p>
      </div>
    </div>

    <!-- Error -->
    <div v-if="mapStatus === 'error'" class="absolute inset-0 flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <i class="pi pi-exclamation-circle text-4xl text-red-400 mb-2"></i>
        <p class="text-sm text-gray-500">Impossible de charger la carte Google Maps.</p>
      </div>
    </div>

    <!-- Legend overlay -->
    <div v-if="mapStatus === 'ready'" class="absolute top-3 left-3 bg-white/90 backdrop-blur rounded-xl px-4 py-3 shadow-md text-xs space-y-1.5">
      <p class="font-semibold text-gray-700 mb-1">Légende</p>
      <div class="flex items-center gap-2"><span class="w-3 h-3 rounded-full bg-green-500 inline-block"></span><span class="text-gray-600">En ligne</span></div>
      <div class="flex items-center gap-2"><span class="w-3 h-3 rounded-full bg-orange-400 inline-block"></span><span class="text-gray-600">En course</span></div>
      <div class="flex items-center gap-2"><span class="w-3 h-3 rounded-full bg-slate-400 inline-block"></span><span class="text-gray-600">Hors ligne</span></div>
    </div>

    <!-- No-location notice -->
    <div v-if="mapStatus === 'ready'" class="absolute bottom-3 right-3 bg-white/90 backdrop-blur rounded-xl px-3 py-1.5 shadow text-xs text-gray-500">
      {{ riders.filter(r => r.location?.lat).length }} / {{ riders.length }} riders localisés
    </div>
  </div>
</template>
